import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { Controller, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { NavLink } from 'react-router-dom';

import Messages from '../defs/messages';
import { usePostForgotPasswordMutation } from '../store/apiSlice';
import { handlePageChange } from '../utils/ally';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
	},
}));

function getRequestErrorMessage(err, fallbackMessage) {
	if (typeof err?.data?.errors?.[0]?.msg === 'string') {
		return err.data.errors[0].msg;
	}

	if (typeof err?.data?.message === 'string' && err.data.message.trim()) {
		return err.data.message;
	}

	return fallbackMessage;
}

function ForgotPassword() {
	const classes = useStyles();
	const headingRef = useRef(null);
	const msg = Messages.FORGOT_PASSWORD;
	const [requestReset, { isLoading }] = usePostForgotPasswordMutation();
	const [serverMessage, setServerMessage] = useState('');
	const [serverError, setServerError] = useState('');

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm({
		defaultValues: {
			email: '',
		},
	});

	useEffect(() => {
		handlePageChange(msg.HEADER_MESSAGE, headingRef);
	}, [msg.HEADER_MESSAGE]);

	const onSubmit = async (values) => {
		setServerMessage('');
		setServerError('');

		try {
			const response = await requestReset(values).unwrap();
			setServerMessage(
				response?.message || Messages.PASSWORD_RESET_REQUEST_ACCEPTED
			);
			reset();
		} catch (err) {
			setServerError(getRequestErrorMessage(err, msg.ERROR_GENERIC));
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>{msg.HEADER_MESSAGE}</title>
			</Helmet>
			<Container maxWidth="sm" className={classes.container}>
				<Paper className={classes.paper}>
					<Typography ref={headingRef} component="h1" variant="h4" role="alert">
						{msg.HEADER_MESSAGE}
					</Typography>
					<Typography component="p">{msg.DESCRIPTION}</Typography>
					<form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
						<Controller
							name="email"
							control={control}
							rules={{
								required: {
									value: true,
									message: Messages.ERROR_EMAIL_EMPTY,
								},
								pattern: {
									value: /\S+@\S+\.\S+/,
									message: Messages.ERROR_EMAIL_INVALID,
								},
							}}
							render={({ field }) => (
								<TextField
									label="Имейл"
									type="email"
									error={!!errors.email}
									helperText={errors?.email?.message}
									autoComplete="email"
									{...field}
								/>
							)}
						/>

						{serverError ? (
							<Typography color="primary" role="alert">
								{serverError}
							</Typography>
						) : null}

						{serverMessage ? (
							<Typography role="status" aria-live="polite">
								{serverMessage}
							</Typography>
						) : null}

						<Grid container spacing={2}>
							<Grid item xs={12} sm="auto">
								<Button
									type="submit"
									color="primary"
									variant="outlined"
									disabled={isLoading}
								>
									{msg.SUBMIT_BUTTON}
								</Button>
							</Grid>
							<Grid item xs={12} sm="auto">
								<Button component={NavLink} to="/sign-in" color="primary">
									{msg.BACK_TO_LOGIN}
								</Button>
							</Grid>
						</Grid>
					</form>
				</Paper>
			</Container>
		</div>
	);
}

export default ForgotPassword;
